// assets/cabinet-align.js — line page headers up with the cabinet in the image below (2026-10-05).
// Left edge of the cabinet as a fraction of the product image width, measured from the
// Desert Oak renders (identical framing across finishes and sizes).
const CABINET_LEFT = {
  'single-bath-vanity': 0.3278,
  '4-drawer-base-cabinet': 0.3269,
  'sink-base-mat': 0.2676,
  'sink-base-shield': 0.2676,
  'sink-base-with-drawer': 0.2685,
  'sink-base-with-drawer-10': 0.2676,
  'sink-base-with-drawer-12': 0.2685,
  'sink-base-with-3-drawer': 0.2685,
  'wall-hung-sink-base': 0.2361,
  'vanity-base-b1dw1d': 0.3454,
  'vanity-base-b1dw2d': 0.2676,
  'vanity-base-b2d': 0.3444,
  'linen-cabinet-2d': 0.3991,
  'linen-cabinet-2d-3dw10': 0.3991,
  'linen-cabinet-2d-3dws': 0.3991,
  'linen-cabinet-3d': 0.3991,
  'linen-cabinet-4d': 0.3269,
  'linen-cabinet-4dw10': 0.3269,
  'linen-cabinet-4d-3dws': 0.3269,
};

/** The <img> inside `container` that covers most of the container's visible box. */
function mainImage(container) {
  const c = container.getBoundingClientRect();
  let best = null;
  let bestArea = 0;
  for (const img of container.querySelectorAll('img')) {
    const r = img.getBoundingClientRect();
    const w = Math.min(r.right, c.right) - Math.max(r.left, c.left);
    const h = Math.min(r.bottom, c.bottom) - Math.max(r.top, c.top);
    const area = Math.max(0, w) * Math.max(0, h);
    if (area > bestArea) {
      bestArea = area;
      best = img;
    }
  }
  return best;
}

/** Rendered picture box of an <img>, honouring object-fit. */
function pictureBox(img) {
  const r = img.getBoundingClientRect();
  const nw = img.naturalWidth;
  const nh = img.naturalHeight;
  const fit = getComputedStyle(img).objectFit;
  if (!nw || !nh || (fit !== 'contain' && fit !== 'cover')) return { left: r.left, width: r.width };
  const s = fit === 'contain' ? Math.min(r.width / nw, r.height / nh) : Math.max(r.width / nw, r.height / nh);
  const w = nw * s;
  return { left: r.left + (r.width - w) / 2, width: w };
}

function reference(el) {
  if (el.dataset.mode === 'product') {
    const gallery = [...document.querySelectorAll('media-gallery')].find(
      (g) => !g.closest('product-card, dialog') && g.getBoundingClientRect().width > 0
    );
    return gallery ? { container: gallery, handle: el.dataset.handle } : null;
  }
  const cards = [...document.querySelectorAll('main product-card')].filter(
    (c) => !c.closest('dialog') && c.getBoundingClientRect().width > 0
  );
  const card = cards[0];
  if (!card) return null;
  const link = card.querySelector('a[href*="/products/"]');
  const handle = link ? new URL(link.href, location.origin).pathname.split('/products/')[1]?.split(/[/?#]/)[0] : '';
  return { container: card, handle };
}

function align(el) {
  const target = el.parentElement;
  if (!target) return;
  const ref = reference(el);
  const ratio = ref ? CABINET_LEFT[ref.handle] : undefined;
  target.style.removeProperty('padding-inline-start');
  // Phones: images are full-bleed and centred, so indenting would just squeeze the header.
  if (!window.matchMedia('(min-width: 750px)').matches) return;
  if (!ref || ratio === undefined) return;
  const img = mainImage(ref.container);
  if (!img) return;
  const box = pictureBox(img);
  const cabinetLeft = box.left + ratio * box.width;
  const cs = getComputedStyle(target);
  const basePad = parseFloat(cs.paddingLeft) || 0;
  const r = target.getBoundingClientRect();
  const contentLeft = r.left + (parseFloat(cs.borderLeftWidth) || 0) + basePad;
  const shift = cabinetLeft - contentLeft;
  if (!(shift > 0) || shift > r.width * 0.6) return;
  target.style.paddingInlineStart = `${Math.round(basePad + shift)}px`;
}

let queued = false;
function alignAll() {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    document.querySelectorAll('cabinet-align').forEach(align);
  });
}

if (!window.__cabinetAlignInit) {
  window.__cabinetAlignInit = true;
  window.addEventListener('resize', alignAll);
  window.addEventListener('load', alignAll);
  document.addEventListener('load', (e) => { if (e.target instanceof HTMLImageElement) alignAll(); }, true);
  document.addEventListener('variant:update', () => setTimeout(alignAll, 50));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', alignAll);
  else alignAll();
  // Late layout shifts (fonts, lazy images, filters) — a few cheap re-checks.
  [300, 1000, 2500].forEach((t) => setTimeout(alignAll, t));
}
